import {
  AutoConcede,
  AutoContinue,
  AutoGimme,
  GimmeDistance,
  GroundConditions,
  HandicapMode,
  HandicapPercentage,
  PinType,
  ScoringType,
  SkyConditions,
  TeeType,
  TimeOfDay,
  WindDirection,
  WindSpeed,
} from "../../enums";
import { prettify } from "../helpers";
import type { Option } from "Models";

/**
 * Dropdown options for the round editor, generated from the **leagues-v2
 * `../enums`** — the authoritative, backend-correct source for `Clubhouse_
 * RoundSetting`. These intentionally do NOT reuse the app-wide
 * `roundConfigEnumsAsReactSelectOptions` (built from `@/types/enums`), because
 * those enums diverge from the league contract (e.g. `SkyConditions`
 * Sunny/Mostly-Sunny vs the old `Sky` Clear/Fair), which would send wrong
 * values. Options are derived from each enum's numeric members so a member
 * rename can't silently desync the list.
 */
function enumOptions(
  e: Record<string, string | number>,
  labels?: Record<number, string>,
): Option<number>[] {
  return Object.entries(e)
    .filter((entry): entry is [string, number] => typeof entry[1] === "number")
    .map(([key, value]) => ({
      value,
      label: labels?.[value] ?? prettify(key),
    }));
}

export const PIN_OPTIONS = enumOptions(PinType, {
  [PinType.NOVICE]: "Easy",
  [PinType.INTERMEDIATE]: "Moderate",
  [PinType.ADVANCED]: "Challenging",
});

export const WIND_SPEED_OPTIONS = enumOptions(WindSpeed, {
  [WindSpeed.CALM]: "Calm (0–3 mph)",
  [WindSpeed.LIGHT]: "Light (4–6 mph)",
  [WindSpeed.MODERATE]: "Moderate (7–9 mph)",
  [WindSpeed.STRONG]: "Strong (10–14 mph)",
  [WindSpeed.VERY_STRONG]: "Very Strong (15+ mph)",
  [WindSpeed.OFF]: "Off",
  [WindSpeed.FIVE_MPH_STEADY]: "5 mph steady",
  [WindSpeed.TEN_MPH]: "10 mph steady",
  [WindSpeed.FIFTEEN_MPH]: "15 mph steady",
  [WindSpeed.TWENTY_MPH]: "20 mph steady",
  [WindSpeed.TWENTY_FIVE_MPH]: "25 mph steady",
  [WindSpeed.THIRTY_MPH]: "30 mph steady",
  [WindSpeed.THIRTY_FIVE_MPH]: "35 mph steady",
});

export const WIND_DIRECTION_OPTIONS = enumOptions(WindDirection, {
  [WindDirection.NORTH]: "North",
  [WindDirection.NORTHEAST]: "Northeast",
  [WindDirection.EAST]: "East",
  [WindDirection.SOUTHEAST]: "Southeast",
  [WindDirection.SOUTH]: "South",
  [WindDirection.SOUTHWEST]: "Southwest",
  [WindDirection.WEST]: "West",
  [WindDirection.NORTHWEST]: "Northwest",
});

export const SKY_OPTIONS = enumOptions(SkyConditions, {
  [SkyConditions.SUNNY]: "Sunny",
  [SkyConditions.MOSTLY_SUNNY]: "Mostly Sunny",
  [SkyConditions.MOSTLY_CLOUDY]: "Mostly Cloudy",
  [SkyConditions.CLOUDY]: "Cloudy",
  [SkyConditions.FOG]: "Foggy",
});

export const GROUND_OPTIONS = enumOptions(GroundConditions, {
  [GroundConditions.NORMAL]: "Normal",
  [GroundConditions.VERY_DRY]: "Very Dry",
  [GroundConditions.DRY]: "Dry",
  [GroundConditions.WET]: "Wet",
  [GroundConditions.VERY_WET]: "Very Wet",
});

export const TIME_OF_DAY_OPTIONS = enumOptions(TimeOfDay, {
  [TimeOfDay.TIME_NONE]: "Any",
});

export const TEE_OPTIONS = enumOptions(TeeType);

export const AUTO_CONCEDE_OPTIONS = enumOptions(AutoConcede, {
  [AutoConcede.OFF]: "Off",
  [AutoConcede.DOUBLE_PAR]: "Double Par",
  [AutoConcede.TEN_STROKES]: "Ten Strokes",
  [AutoConcede.BOGEY]: "Bogey",
  [AutoConcede.DOUBLE_BOGEY]: "Double Bogey",
  [AutoConcede.TRIPLE_BOGEY]: "Triple Bogey",
});

export const AUTO_CONTINUE_OPTIONS = enumOptions(AutoContinue, {
  [AutoContinue.OFF]: "Off",
  [AutoContinue.INSTANT]: "Instant",
  [AutoContinue.ONE_SECOND]: "1 second",
  [AutoContinue.FOUR_SECONDS]: "4 seconds",
  [AutoContinue.SEVEN_SECONDS]: "7 seconds",
  [AutoContinue.TEN_SECONDS]: "10 seconds",
  [AutoContinue.FIFTEEN_SECONDS]: "15 seconds",
});

export const AUTO_GIMME_OPTIONS = enumOptions(AutoGimme, {
  [AutoGimme.OFF]: "Off",
  [AutoGimme.ONESTROKE]: "1 Stroke",
  [AutoGimme.TWOSTROKE]: "2 Strokes",
  [AutoGimme.THREESTROKE]: "3 Strokes",
});

export const GIMME_DISTANCE_OPTIONS = enumOptions(GimmeDistance, {
  [GimmeDistance.OFF]: "Off",
  [GimmeDistance.THREE]: "3 ft",
  [GimmeDistance.SIX]: "6 ft",
  [GimmeDistance.NINE]: "9 ft",
  [GimmeDistance.TWELVE]: "12 ft",
});

export const HANDICAP_MODE_OPTIONS = enumOptions(HandicapMode, {
  [HandicapMode.ABSOLUTE]: "Absolute (vs course)",
  [HandicapMode.RELATIVE]: "Relative (vs players)",
  [HandicapMode.OFF]: "Off",
  [HandicapMode.PLAYER]: "Player",
});

export const HANDICAP_PERCENTAGE_OPTIONS = enumOptions(HandicapPercentage, {
  [HandicapPercentage.ONE_HUNDRED]: "100%",
  [HandicapPercentage.NINETY]: "90%",
  [HandicapPercentage.EIGHTY]: "80%",
  [HandicapPercentage.SEVENTY]: "70%",
});

export const SCORING_TYPE_OPTIONS = enumOptions(ScoringType);

// Common green-speed (stimp) presets; stimpSetting is a decimal on the wire.
export const STIMP_OPTIONS: Option<number>[] = [
  { value: 7, label: "7.0 — Slow" },
  { value: 8.5, label: "8.5 — Medium" },
  { value: 10, label: "10.0 — Fast" },
  { value: 11.5, label: "11.5 — Very Fast" },
  { value: 13, label: "13.0 — Tournament" },
];
